// src/components/leave/LeaveManagementSidebar.jsx
import React, { useState, useEffect } from 'react';
import { leaveAPI } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import '../../styles/LeaveManagement.css';
import LeaveBalanceSummary from './LeaveBalanceSummary';

const LeaveManagementSidebar = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('apply');
  const [myLeaves, setMyLeaves] = useState([]);
  const [leaveStats, setLeaveStats] = useState({
    my_pending: 0,
    my_approved: 0,
    my_rejected: 0
  });
  const [loading, setLoading] = useState(false);

  // Form state for applying leave
  const [formData, setFormData] = useState({
    leave_type: '',
    from_date: '',
    to_date: '',
    reason: ''
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [balanceCheck, setBalanceCheck] = useState(null);
  const [checkingBalance, setCheckingBalance] = useState(false);

  useEffect(() => {
    if (activeTab === 'status') {
      loadMyLeaves();
    }
    loadLeaveStats();
  }, [activeTab]);

  const loadMyLeaves = async () => {
    try {
      setLoading(true);
      const data = await leaveAPI.getMyLeaves();
      const leavesArray = data?.results || data || [];
      setMyLeaves(Array.isArray(leavesArray) ? leavesArray : []);
    } catch (error) {
      console.error('Error loading my leaves:', error);
      setMyLeaves([]);
    } finally {
      setLoading(false);
    }
  };

  const loadLeaveStats = async () => {
    try {
      const stats = await leaveAPI.getLeaveStats();
      setLeaveStats(stats);
    } catch (error) {
      console.error('Error loading leave stats:', error);
    }
  };

  const handleChange = (e) => {
    const newFormData = { ...formData, [e.target.name]: e.target.value };
    handleDateOrTypeChange(newFormData);
  };


  const handleDateOrTypeChange = async (newFormData) => {
  setFormData(newFormData);
  // Auto-check balance when all 3 fields are filled
  if (newFormData.leave_type && newFormData.from_date && newFormData.to_date) {
    try {
      setCheckingBalance(true);
      const check = await leaveAPI.checkBalance(
        newFormData.leave_type,
        newFormData.from_date,
        newFormData.to_date
      );
      setBalanceCheck(check);
    } catch (err) {
      setBalanceCheck(null);
    } finally {
      setCheckingBalance(false);
    }
  } else {
    setBalanceCheck(null);
  }
};


    const handleSubmit = async (e) => {
      e.preventDefault();
      setError('');
      setSuccess('');
      setSubmitting(true);

      try {
        const response = await leaveAPI.applyLeave(formData);
        setSuccess(response.message || 'Leave application submitted successfully');

        // Reset form
        setFormData({
          leave_type: '',
          from_date: '',
          to_date: '',
          reason: ''
        });

        // Reload stats
        loadLeaveStats();

        // Switch to status tab after 2 seconds
        setTimeout(() => {
          setActiveTab('status');
          setSuccess('');
        }, 2000);
      } catch (err) {
        console.error('Leave application error:', err);

        // FIXED: Extract error message with better handling
        let errorMsg = 'Failed to submit leave application';

        if (err.response?.data) {
          if (err.response.data.error) {
            errorMsg = err.response.data.error;
          } else if (err.response.data.message) {
            errorMsg = err.response.data.message;
          } else if (err.response.data.detail) {
            errorMsg = err.response.data.detail;
          } else if (typeof err.response.data === 'object') {
            // Handle field-specific errors
            const errors = Object.entries(err.response.data)
              .map(([field, messages]) => {
                const msg = Array.isArray(messages) ? messages.join(', ') : messages;
                return `${field}: ${msg}`;
              })
              .join('; ');
            errorMsg = errors || errorMsg;
          }
        } else if (err.message) {
          errorMsg = err.message;
        }

        setError(errorMsg);
      } finally {
        setSubmitting(false);
      }
    };


  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'PENDING': 'badge-orange',
      'APPROVED': 'badge-green',
      'REJECTED': 'badge-red'
    };
    return `badge ${statusMap[status] || 'badge-gray'}`;
  };

  return (
    <div className="leave-management-sidebar">
      <div className="sidebar-header">
        <h2>Leave Management</h2>
      </div>

      {/* Stats Summary */}
      <div className="leave-stats-summary">
        <div className="stat-item pending">
          <span className="stat-value">{leaveStats.my_pending}</span>
          <span className="stat-label">Pending</span>
        </div>
        <div className="stat-item approved">
          <span className="stat-value">{leaveStats.my_approved}</span>
          <span className="stat-label">Approved</span>
        </div>
        <div className="stat-item rejected">
          <span className="stat-value">{leaveStats.my_rejected}</span>
          <span className="stat-label">Rejected</span>
        </div>
      </div>

      {/* Tabs */}
      <div className="leave-tabs">
        <button
          className={`leave-tab ${activeTab === 'apply' ? 'active' : ''}`}
          onClick={() => setActiveTab('apply')}
        >
          Apply Leave
        </button>
        <button
          className={`leave-tab ${activeTab === 'status' ? 'active' : ''}`}
          onClick={() => setActiveTab('status')}
        >
          My Leave Status
        </button>
        <button
          className={`leave-tab ${activeTab === 'balance' ? 'active' : ''}`}
          onClick={() => setActiveTab('balance')}
        >
          My Balance
        </button>
      </div>

      {/* Tab Content */}
      <div className="leave-tab-content">
        {activeTab === 'apply' && (
          <div className="apply-leave-form">
            {error && <div className="error-message">{error}</div>}
            {success && <div className="success-message">{success}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="leave_type">Leave Type *</label>
                <select
                  id="leave_type"
                  name="leave_type"
                  value={formData.leave_type}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select Leave Type</option>
                  <option value="CL">Casual Leave (CL)</option>
                  <option value="EL">Earned Leave (EL)</option>
                  <option value="RH">Restricted Holiday (RH)</option>
                  <option value="HPL">Half Pay Leave (HPL)</option>
                  <option value="CCL">Child Care Leave (CCL)</option>
                  <option value="COL">Compensatory Leave (COL)</option>
                  <option value="MEDICAL">Medical Leave</option>
                  <option value="SPECIAL">Special Leave</option>
                  <option value="ONE_THIRD">One Third</option>
                  <option value="OTHERS">Others</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="from_date">From Date *</label>
                <input
                  type="date"
                  id="from_date"
                  name="from_date"
                  value={formData.from_date}
                  onChange={handleChange}
                  min={new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="to_date">To Date *</label>
                <input
                  type="date"
                  id="to_date"
                  name="to_date"
                  value={formData.to_date}
                  onChange={handleChange}
                  min={formData.from_date || new Date().toISOString().split('T')[0]}
                  required
                />
              </div>

              {checkingBalance && (
                  <div style={{ padding: '0.5rem', color: '#6b7280', fontSize: '0.85rem' }}>
                    Checking balance…
                  </div>
                )}
                {balanceCheck && !checkingBalance && (
                  <div style={{
                    padding: '0.75rem',
                    borderRadius: '6px',
                    marginBottom: '0.5rem',
                    background: balanceCheck.will_go_negative ? '#fff7ed' : '#f0fdf4',
                    border: `1px solid ${balanceCheck.will_go_negative ? '#fed7aa' : '#bbf7d0'}`,
                    fontSize: '0.85rem'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
                      <span><strong>Current Balance:</strong> {balanceCheck.current_balance} days</span>
                      <span><strong>Requested:</strong> {balanceCheck.requested_days} days</span>
                    </div>
                    {balanceCheck.will_go_negative && (
                      <div style={{ color: '#c2410c', fontWeight: 600, marginBottom: '0.3rem' }}>
                        ⚠️ {balanceCheck.warning_message}
                      </div>
                    )}
                    {!balanceCheck.will_go_negative && (
                      <div style={{ color: '#15803d' }}>
                        ✅ Fully within balance. No salary deduction.
                      </div>
                    )}
                    {!balanceCheck.allow_negative && balanceCheck.will_go_negative && (
                      <div style={{ color: '#dc2626', marginTop: '0.3rem', fontWeight: 600 }}>
                        🚫 This leave type does not allow negative balance. Please reduce the leave days.
                      </div>
                    )}
                  </div>
                )}

              <div className="form-group">
                <label htmlFor="reason">Reason *</label>
                <textarea
                  id="reason"
                  name="reason"
                  rows="4"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Provide a detailed reason for your leave request"
                  required
                />
              </div>

              <button type="submit" className="btn-primary btn-block" disabled={submitting}>
                {submitting ? 'Submitting...' : 'Submit Leave Application'}
              </button>
            </form>
          </div>
        )}

        {activeTab === 'status' && (
          <div className="my-leaves-list">
            {loading ? (
              <div className="loading-container">
                <div className="spinner"></div>
                <p>Loading your leaves...</p>
              </div>
            ) : myLeaves.length === 0 ? (
              <div className="empty-state">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <p>No leave applications yet</p>
                <button className="btn-link" onClick={() => setActiveTab('apply')}>
                  Apply for Leave
                </button>
              </div>
            ) : (
              <div className="leaves-timeline">
                {myLeaves.map((leave) => (
                  <div key={leave.id} className={`leave-card leave-${leave.status.toLowerCase()}`}>
                    <div className="leave-card-header">
                      <h4>{leave.leave_type_display}</h4>
                      <span className={getStatusBadgeClass(leave.status)}>
                        {leave.status_display}
                      </span>
                    </div>
                    <div className="leave-card-body">
                      <div className="leave-dates">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <span>{formatDate(leave.from_date)} - {formatDate(leave.to_date)}</span>
                        <span className="duration-badge">{leave.duration_days} day{leave.duration_days > 1 ? 's' : ''}</span>
                      </div>
                      <p className="leave-reason">{leave.reason}</p>
                      <div className="leave-meta">
                        <span className="approver-info">
                          <strong>Approver:</strong> {leave.approver_name}
                        </span>
                        {leave.approved_by_name && (
                          <span className="approved-by-info">
                            <strong>{leave.status === 'APPROVED' ? 'Approved' : 'Rejected'} by:</strong> {leave.approved_by_name}
                          </span>
                        )}
                      </div>
                      {leave.rejection_reason && (
                        <div className="rejection-reason">
                          <strong>Reason for Rejection:</strong>
                          <p>{leave.rejection_reason}</p>
                        </div>
                      )}
                      <div className="leave-timestamp">
                        Applied on {formatDate(leave.created_at)}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
        {activeTab === 'balance' && (
          <div style={{ padding: '0.5rem 0' }}>
            <LeaveBalanceSummary />
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaveManagementSidebar;